import { Document, Packer, Paragraph, TextRun } from 'docx';
import { groupRunsIntoLines } from './buildCueContent.js';

/**
 * @param {import('./buildCueContent.js').TranscriptRun} run
 * @returns {TextRun[]}
 */
function textRunsFromRun(run) {
    return run.text.split('\n').map((text, index) => new TextRun({
        text,
        bold: Boolean(run.bold),
        italics: Boolean(run.italic),
        break: index > 0 ? 1 : undefined,
    }));
}

/**
 * @param {import('./buildCueContent.js').TranscriptRun[]} runs
 * @returns {Paragraph[]}
 */
function paragraphsFromRuns(runs) {
    const lines = groupRunsIntoLines(runs);
    /** @type {Paragraph[]} */
    const paragraphs = [];
    lines.forEach((lineRuns) => {
        /** @type {TextRun[]} */
        const children = [];
        lineRuns.forEach((run, index) => {
            if (index > 0) {
                const prev = lineRuns[index - 1];
                if (!/\s$/.test(prev.text)) {
                    children.push(new TextRun({ text: '\t' }));
                }
            }
            children.push(...textRunsFromRun(run));
        });
        paragraphs.push(new Paragraph({ children }));
        if (lineRuns[lineRuns.length - 1]?.breakAfter === 2) {
            paragraphs.push(new Paragraph({ children: [] }));
        }
    });
    return paragraphs;
}

/**
 * @param {import('./buildCued.js').TranscriptBlock[]} blocks
 * @param {string} title
 * @returns {Promise<Blob>}
 */
export async function buildDocx(blocks, title) {
    /** @type {Paragraph[]} */
    const children = [];

    for (const block of blocks) {
        if (block.kind === 'plain') {
            for (const line of block.lines || []) {
                children.push(
                    new Paragraph({
                        children: [new TextRun({ text: line })],
                    })
                );
            }
            children.push(new Paragraph({ children: [] }));
            continue;
        }

        if (block.kind === 'cue' || block.kind === 'prose') {
            const runs = block.runs || [];
            children.push(...paragraphsFromRuns(runs));
            if (runs[runs.length - 1]?.breakAfter !== 2) {
                children.push(new Paragraph({ children: [] }));
            }
        }
    }

    const doc = new Document({
        creator: 'Closed Caption Creator Inc.',
        title,
        sections: [
            {
                properties: {},
                children,
            },
        ],
    });

    return Packer.toBlob(doc);
}
